// ILF Product Media Gallery — <ilf-product-media-gallery>
// Swiper-based product media gallery with thumbnail strip, selectable
// effects (including the UI Initiative carousel plugin), orifice-based media
// filtering, and native variant-change integration: global.js calls
// setActiveMedia(mediaId) on this element after a variant switch. Media
// playback is handled per gallery: leaving a slide, or starting a slide's
// deferred media, pauses the media inside this element only, so autoplaying
// videos elsewhere on the page (hero previews, texture strips) keep running.
// There is deliberately no page scrolling here.

if (!customElements.get('ilf-product-media-gallery')) {
  class IlfProductMediaGallery extends HTMLElement {
    static observedAttributes = ['data-ilf-selected-orifice'];

    // Effect registry: maps the section setting to Swiper params. To add a
    // UI Initiative effect later: drop its min.js/min.css into assets, load
    // them from ilf-product-media-gallery.liquid, add a settings option, and add
    // an entry here.
    effectParams(effect) {
      switch (effect) {
        case 'fade':
          // fade and cube use virtualTranslate: spaceBetween must be 0 or every
          // slide accumulates a horizontal offset (visible on alternating faces).
          return { effect: 'fade', spaceBetween: 0, fadeEffect: { crossFade: true } };
        case 'cube':
          return { effect: 'cube', spaceBetween: 0, cubeEffect: { shadow: false, slideShadows: false } };
        case 'coverflow':
          return {
            effect: 'coverflow',
            spaceBetween: 0,
            centeredSlides: true,
            coverflowEffect: { rotate: 30, slideShadows: false },
          };
        case 'carousel':
          if (typeof EffectCarousel === 'undefined') return {};
          return {
            modules: [EffectCarousel],
            effect: 'carousel',
            centeredSlides: true,
            slidesPerView: 'auto',
            carouselEffect: {
              sideSlides: parseInt(this.dataset.carouselSideSlides, 10) || 2,
              opacityStep: (parseInt(this.dataset.carouselOpacityStep, 10) || 33) / 100,
              scaleStep: (parseInt(this.dataset.carouselScaleStep, 10) || 20) / 100,
            },
          };
        default:
          return {};
      }
    }

    connectedCallback() {
      if (this.initialised) return;
      if (typeof Swiper === 'undefined') {
        // Swiper's deferred script has not executed yet (it is rendered before
        // this file, but be defensive about injection order).
        window.addEventListener('load', () => this.connectedCallback(), { once: true });
        return;
      }
      this.initialised = true;

      this.stageEl = this.querySelector('.gallery-stage');
      this.thumbsEl = this.querySelector('.gallery-thumbs');
      this.counterCurrent = this.querySelector('[data-gallery-current]');
      this.counterTotal = this.querySelector('[data-gallery-total]');

      // Master copies of every slide/thumb, in original order — filtering
      // detaches non-matching nodes and re-attaches them later.
      this.allSlides = [...this.stageEl.querySelectorAll('.gallery-slide')];
      this.allThumbs = [...this.thumbsEl.querySelectorAll('.gallery-thumb')];

      this.pendingFilterTimer = null;
      this.applyFilter();
      this.initSwipers(this.mediaIndex(this.dataset.initialMedia));
      this.classList.add('is-initialised');

      // Fullscreen mode: the SAME slider expands over the page — no duplicate
      // DOM, no second image fetch, state continuity for free. While open the
      // element is a modal dialog: role/aria-modal swap in, Tab cycles inside
      // it, Escape and backdrop clicks close it, and pinch / double-tap zoom
      // is enabled on the image slides.
      this.onFullscreenOpen = this.onFullscreenOpen.bind(this);
      this.onFullscreenClose = this.onFullscreenClose.bind(this);
      this.onFullscreenKeydown = this.onFullscreenKeydown.bind(this);
      this.onFullscreenBackdropClick = this.onFullscreenBackdropClick.bind(this);
      this.zoomButton = this.querySelector('[data-gallery-fullscreen-open]');
      this.closeButton = this.querySelector('[data-gallery-fullscreen-close]');
      this.zoomButton?.addEventListener('click', this.onFullscreenOpen);
      this.closeButton?.addEventListener('click', this.onFullscreenClose);

      // A poster click loads that slide's media (<ilf-deferred-media>, which
      // pauses nothing itself): pause the gallery's other media first.
      this.onDeferredMediaLoad = this.onDeferredMediaLoad.bind(this);
      this.addEventListener('ilf:deferred-media:load', this.onDeferredMediaLoad);
    }

    disconnectedCallback() {
      clearTimeout(this.pendingFilterTimer);
      this.onFullscreenClose();
      this.zoomButton?.removeEventListener('click', this.onFullscreenOpen);
      this.closeButton?.removeEventListener('click', this.onFullscreenClose);
      this.removeEventListener('ilf:deferred-media:load', this.onDeferredMediaLoad);
      this.mainSwiper?.destroy(true, false);
      this.thumbsSwiper?.destroy(true, false);
      this.initialised = false;
    }

    onDeferredMediaLoad() {
      this.pauseMedia();
    }

    onFullscreenOpen() {
      if (this.classList.contains('is-fullscreen')) return;

      // Upgrade responsive images for viewport-sized rendering: with a bigger
      // sizes value the browser fetches a larger srcset candidate and swaps it
      // in without dropping the currently displayed one. One-way — done once.
      if (!this.sizesUpgraded) {
        this.sizesUpgraded = true;
        this.allSlides.forEach((slide) => {
          slide.querySelectorAll('img[sizes]').forEach((img) => {
            img.sizes = '100vw';
          });
        });
      }

      this.classList.add('is-fullscreen');
      document.body.classList.add('ilf-product-gallery-open');
      this.regionLabel = this.getAttribute('aria-label');
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (this.dataset.fullscreenLabel) this.setAttribute('aria-label', this.dataset.fullscreenLabel);
      document.addEventListener('keydown', this.onFullscreenKeydown);
      this.addEventListener('click', this.onFullscreenBackdropClick);
      // The container just changed size drastically — remeasure.
      this.mainSwiper?.update();
      this.mainSwiper?.zoom.enable();
      this.closeButton?.focus();
    }

    onFullscreenClose() {
      if (!this.classList.contains('is-fullscreen')) return;
      this.mainSwiper?.zoom.out();
      this.mainSwiper?.zoom.disable();
      this.classList.remove('is-fullscreen');
      document.body.classList.remove('ilf-product-gallery-open');
      this.setAttribute('role', 'region');
      this.removeAttribute('aria-modal');
      if (this.regionLabel) this.setAttribute('aria-label', this.regionLabel);
      document.removeEventListener('keydown', this.onFullscreenKeydown);
      this.removeEventListener('click', this.onFullscreenBackdropClick);
      this.mainSwiper?.update();
      this.thumbsSwiper?.update();
      this.zoomButton?.focus();
    }

    onFullscreenKeydown(event) {
      if (event.key === 'Escape') {
        this.onFullscreenClose();
        return;
      }
      if (event.key !== 'Tab') return;
      // Focus trap: Tab cycles through the controls visible in fullscreen
      // (thumbnails and the open button are display: none there).
      const focusable = [
        ...this.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), video, [tabindex]:not([tabindex^="-"])'
        ),
      ].filter((el) => el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!this.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFullscreenBackdropClick(event) {
      // Close on clicks in the empty area only — not on media, controls, or
      // the tail end of a swipe (Swiper clears allowClick after a real drag).
      if (event.target.closest('img, button, video, iframe, model-viewer, deferred-media, ilf-deferred-media, ilf-product-model, .gallery-counter')) return;
      if (this.mainSwiper && this.mainSwiper.allowClick === false) return;
      this.onFullscreenClose();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.initialised || oldValue === newValue) return;
      // Do not re-filter immediately: the variant flow is about to call
      // setActiveMedia with the new featured media, and filtering first would
      // blank the stage. Wait for that call, with a fallback for variants
      // that have no featured media.
      clearTimeout(this.pendingFilterTimer);
      this.pendingFilterTimer = setTimeout(() => this.rebuild(), 1200);
    }

    get selectedOrifice() {
      return this.getAttribute('data-ilf-selected-orifice') || '';
    }

    matchesFilter(el) {
      const tag = el.dataset.ilfMediaOrifice;
      // 'u' = universal media: tagged, but visible for every orifice.
      return !tag || tag === 'u' || !this.selectedOrifice || tag === this.selectedOrifice;
    }

    applyFilter() {
      const stageWrapper = this.stageEl.querySelector('.swiper-wrapper');
      const thumbsWrapper = this.thumbsEl.querySelector('.swiper-wrapper');
      stageWrapper.replaceChildren(...this.allSlides.filter((slide) => this.matchesFilter(slide)));
      thumbsWrapper.replaceChildren(...this.allThumbs.filter((thumb) => this.matchesFilter(thumb)));
      if (this.counterTotal) this.counterTotal.textContent = String(stageWrapper.children.length);
    }

    // Full re-init on every slide-set change. Effects with virtualTranslate
    // (cube, fade) and the carousel plugin precompute per-slide 3D state, and
    // loop mode reorders slide DOM — swiper.update() cannot repair either, so
    // destroying and rebuilding from the filtered DOM is the only reliable path.
    rebuild(targetMediaId) {
      clearTimeout(this.pendingFilterTimer);
      this.pendingFilterTimer = null;

      const currentMediaId =
        targetMediaId || this.mainSwiper?.slides?.[this.mainSwiper.activeIndex]?.dataset.mediaId;

      this.mainSwiper?.destroy(true, true);
      this.thumbsSwiper?.destroy(true, true);
      this.mainSwiper = null;
      this.thumbsSwiper = null;

      this.applyFilter();
      this.initSwipers(this.mediaIndex(currentMediaId));
    }

    // Index of a media id within the currently attached (filtered) slides.
    mediaIndex(mediaId) {
      if (!mediaId) return 0;
      const slides = [...this.stageEl.querySelectorAll('.gallery-slide')];
      return Math.max(0, slides.findIndex((slide) => slide.dataset.mediaId === mediaId));
    }

    initSwipers(initialIndex = 0) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const loop = this.dataset.loop === 'true';
      // Swiper's default focusableElements includes `button`, which blocks
      // swipe initiation. Our video/model slides are fully covered by a
      // deferred-media poster <button> and every thumbnail IS a <button>, so
      // mouse swipes died on exactly those slides. `video` stays protected so
      // scrubbing a playing video's timeline never drags the slider.
      const focusableElements = 'input, select, option, textarea, video, label';

      this.thumbsSwiper = new Swiper(this.thumbsEl, {
        slidesPerView: 4,
        spaceBetween: 10,
        watchSlidesProgress: true,
        watchOverflow: true,
        focusableElements: focusableElements,
        breakpoints: {
          750: { slidesPerView: 5 },
        },
        navigation: {
          prevEl: this.querySelector('.thumbs-nav--prev'),
          nextEl: this.querySelector('.thumbs-nav--next'),
        },
      });

      this.mainSwiper = new Swiper(this.stageEl, {
        slidesPerView: 1,
        spaceBetween: 10,
        speed: reducedMotion ? 0 : 350,
        loop: loop,
        initialSlide: initialIndex,
        watchOverflow: true,
        grabCursor: true,
        focusableElements: focusableElements,
        navigation: {
          prevEl: this.querySelector('.gallery-nav--prev'),
          nextEl: this.querySelector('.gallery-nav--next'),
        },
        thumbs: { swiper: this.thumbsSwiper },
        a11y: { enabled: true },
        keyboard: { enabled: true, onlyInViewport: true },
        // Slide images beyond the initial one carry loading="lazy"; Swiper
        // lifts the attribute on the neighbours of the active slide so they
        // are fetched before they scroll into view.
        lazyPreloadPrevNext: 2,
        // Pinch / double-tap zoom on the image slides (.swiper-zoom-container
        // in the markup). Off in the inline gallery, enabled while fullscreen.
        zoom: { enabled: false, maxRatio: 3 },
        on: {
          slideChange: () => {
            this.updateCounter();
            // Leaving a slide pauses the gallery's own media only.
            this.pauseMedia();
          },
        },
        ...this.effectParams(this.dataset.effect),
      });

      // A rebuild while fullscreen (pending orifice filter) keeps zoom on.
      if (this.classList.contains('is-fullscreen')) this.mainSwiper.zoom.enable();
      this.updateCounter();
    }

    updateCounter() {
      if (!this.counterCurrent || !this.mainSwiper) return;
      const index = this.mainSwiper.realIndex ?? this.mainSwiper.activeIndex ?? 0;
      this.counterCurrent.textContent = String(index + 1);
    }

    // Pauses every playing media inside the gallery: native videos, YouTube /
    // Vimeo embeds (rendered with the js-youtube / js-vimeo classes) and 3D
    // models. Scoped to this element on purpose: a page-wide pause would also
    // stop autoplaying videos in other sections, which have nothing to do with
    // the gallery's slide state.
    pauseMedia() {
      this.querySelectorAll('.js-youtube').forEach((frame) => {
        frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      this.querySelectorAll('.js-vimeo').forEach((frame) => {
        frame.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.querySelectorAll('ilf-product-model, product-model').forEach((model) => {
        model.modelViewerUI?.pause();
      });
    }

    // Contract with global.js VariantSelects: called with
    // `${sectionId}-${featured_media.id}` after a variant change.
    setActiveMedia(mediaId) {
      if (!this.initialised) return;
      // A pending orifice filter waits for this moment: apply it and land
      // directly on the new variant's media — no blank frame in between.
      if (this.pendingFilterTimer) {
        this.rebuild(mediaId);
      } else {
        this.slideToMedia(mediaId);
      }
    }

    slideToMedia(mediaId, speed) {
      if (!this.mainSwiper) return;
      // In loop mode Swiper reorders slide DOM, so a slide's position in the
      // document is not its logical index — data-swiper-slide-index is.
      const slide = [...this.stageEl.querySelectorAll('.gallery-slide')].find(
        (el) => el.dataset.mediaId === mediaId
      );
      if (!slide) return;
      if (this.mainSwiper.params.loop) {
        const realIndex = parseInt(slide.dataset.swiperSlideIndex ?? '0', 10);
        this.mainSwiper.slideToLoop(realIndex, speed);
      } else {
        this.mainSwiper.slideTo(this.mediaIndex(mediaId), speed);
      }
    }
  }

  customElements.define('ilf-product-media-gallery', IlfProductMediaGallery);
}

